import { useQuery } from '@vue/apollo-composable';
import { computed, ref, watch, watchEffect } from 'vue';
import { useStore } from 'vuex';

import { useFischer } from '@/composables/fischerPlugin';
import GET_HANDLING_FEE_LIMIT from '@/graphql/getHandlingFeeLimit.gql';

import { useHandlingFeeCookie } from './handlingFeeCookie';

export const useHandlingFeeSetup = () => {
  const store = useStore();
  const fischer = useFischer();

  const {
    clearHandlingFeeLimitCookie,
    getHandlingFeeLimitCookie,
    setHandlingFeeLimitCookie,
    getHandlingFeeLimitFormatted,
  } = useHandlingFeeCookie();

  const handlingFeeLimit = ref<string | null>(null);
  const handlingFeeBannerScrollHandlerEnabled = ref<boolean>(false);

  const requestHandlingFeeBanner = computed<boolean>(
    () => store.getters['navigation/requestHandlingFeeBanner'],
  );
  const enableHandlingFeeBanner = computed<boolean>(
    () => store.getters['navigation/enableHandlingFeeBanner'] === true,
  );
  const isScrolledOutHandlingFeeBanner = computed<boolean>(
    () => store.getters['navigation/isScrolledOutHandlingFeeBanner'],
  );

  const checkHandlingFeeRequested = () => {
    if (enableHandlingFeeBanner.value && !fischer.user.getSalesforce()) {
      const currentUrl = fischer.uri();
      if (
        fischer.isCategoryPage() ||
        fischer.isFamilyPage() ||
        fischer.isVariantPage() ||
        fischer.pages.products().url === currentUrl ||
        fischer.pages.cart().url === currentUrl ||
        fischer.pages.checkout().url === currentUrl
      ) {
        store.commit('navigation/REQUEST_HANDLING_FEE_BANNER', true);

        if (!IS_SSR) {
          handlingFeeBannerScrollHandlerEnabled.value = true;
        }
      }
    }
  };

  watchEffect(() => {
    if (IS_SSR || !handlingFeeBannerScrollHandlerEnabled.value) {
      return;
    }

    const scrollPosition = store.getters['layout/getWindowScrollPos'];
    const hideThreshold = isScrolledOutHandlingFeeBanner.value ? 250 : 300; // make sure we do not cycle between 'scrolled in' and 'scrolled out' because we are at the edge
    const hideBanner = scrollPosition.y > hideThreshold;
    if (hideBanner !== isScrolledOutHandlingFeeBanner.value) {
      store.commit('navigation/IS_SCROLLED_OUT_HANDLING_FEE_BANNER', hideBanner);
    }
  });

  const setHandlingFeeLimit = (value: string | null) => {
    handlingFeeLimit.value = value;
    store.commit('navigation/ENABLE_HANDLING_FEE_BANNER', value !== null);
    checkHandlingFeeRequested();
  };

  if (fischer.shop.isClosedShop() && fischer.user.accessToken()) {
    setHandlingFeeLimit(getHandlingFeeLimitFormatted());
  } else {
    clearHandlingFeeLimitCookie();
  }

  const enabled = computed<boolean>(() =>
    Boolean(
      !IS_SSR &&
        !getHandlingFeeLimitCookie()?.noBanner &&
        handlingFeeLimit.value === null &&
        fischer.shop.isClosedShop() &&
        (fischer.user.accessToken() || fischer.user.getAssistedService()?.user),
    ),
  );
  const { result, onError } = useQuery(
    GET_HANDLING_FEE_LIMIT,
    {
      baseSiteId: fischer.baseSiteId(),
      userId: fischer.user.id(),
      accessToken: fischer.user.accessToken(),
    },
    { enabled },
  );

  onError((error: any) => {
    console.error('could not get handling fee limit', error);
  });
  const queryResult = computed(() => result.value?.getHandlingFeeLimit);

  watch(
    queryResult,
    (value: { handlingFeeLimit: number; currency: { isocode: string } } | null) => {
      const handlingFeeLimitResult = value?.handlingFeeLimit;
      if (handlingFeeLimitResult) {
        setHandlingFeeLimitCookie(handlingFeeLimitResult, value.currency?.isocode, false);
        setHandlingFeeLimit(getHandlingFeeLimitFormatted());
      }
    },
  );

  return {
    handlingFeeLimit,
    requestHandlingFeeBanner,
  };
};
